"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { browserClient } from "@/lib/supabase/clients/browser-client";
import { openAuthDialog } from "@/app/_components/account/AuthDialogProvider";
import { getOrCreateChatSession, loadChatMessages } from "@/app/fletes/[city]/transportista/[carrier]/_actions/chat";
import { consumePendingContactReveal } from "@/app/fletes/[city]/transportista/[carrier]/_components/chat/toolsComponents/InlineContactCard";
import posthog from "posthog-js";
import { useFeatureFlagEnabled } from "posthog-js/react";

export type PanelMode = "static" | "auth-gate" | "live";

// Per-session message cap (#486) — must match SESSION_USER_MSG_CAP in the API.
const MAX_USER_MESSAGES = 15;

export interface UseConciergeChatOptions {
  /** "" on the home panel; the carrier's id on a carrier page. */
  carrierId: string;
  carrierName: string;
  /** Analytics surface — "home" or "carrier". */
  page: "home" | "carrier";
}

/**
 * All the concierge-panel behavior both HomeChatPanel and the carrier ChatPanel
 * share: anon/auth-gated session init, the free-anon feature flag, message caps,
 * the auth-state listener (sign-in reveal / sign-out reset), autoscroll, and the
 * send handlers. The two panels differ only in layout + copy, which stays in the
 * components; this hook is the single home for the logic (and the loading-state
 * fix: `isSending` is derived so a queued message shows a pending bubble + typing
 * dots immediately, before the async anon-init finishes).
 */
export function useConciergeChat({ carrierId, carrierName, page }: UseConciergeChatOptions) {
  const [mode, setMode] = useState<PanelMode>("static");
  const [authChecked, setAuthChecked] = useState(false);
  const [initialUser, setInitialUser] = useState<{ id: string } | null>(null);
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [limitReached, setLimitReached] = useState<"session" | "ip" | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Carrier fields go on analytics events only when we're on a carrier page.
  const carrierProps = carrierId
    ? { carrier_id: carrierId, carrier_name: carrierName }
    : {};

  // Red Reley v1 (#486): when enabled, chat runs free for anonymous visitors (a
  // Supabase anonymous sign-in) instead of gating on the first message. Off /
  // unresolved → legacy first-message auth gate. NEXT_PUBLIC_FREE_ANON_CHAT=1
  // forces it on (local dev / secondary kill-switch).
  const freeAnonChatFlag = useFeatureFlagEnabled("free-anon-chat");
  const freeAnonChat =
    process.env.NEXT_PUBLIC_FREE_ANON_CHAT === "1" ? true : freeAnonChatFlag;
  const initStartedRef = useRef(false);
  // Keep the skeleton up until the flag settles, so a free-anon visitor never
  // flashes the static landing before the live session initializes.
  const [graceElapsed, setGraceElapsed] = useState(false);
  const flagSettled = freeAnonChat !== undefined || graceElapsed;

  // Transport is created once at mount; read sessionId live from a ref via a body
  // function (resolved per-send) so the real id set later by init is sent, not null.
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const chat = useChat({
    id: sessionId || undefined,
    // body() reads sessionIdRef.current per-send (a user action), never during
    // render, so the ref access here is safe.
    // eslint-disable-next-line react-hooks/refs
    transport: new DefaultChatTransport({
      api: "/api/chat",
      body: () => ({
        sessionId: sessionIdRef.current,
        carrierId,
        carrierName,
      }),
    }),
    // A 429 from the API (per-IP cap, or a session-cap race) surfaces here.
    onError: () => setLimitReached((prev) => prev ?? "ip"),
  });

  // Keep the latest chat instance in a ref so the auth-listener and pending-send
  // effects can call chat.sendMessage / setMessages without listing `chat` as a
  // dependency — its identity changes every render, which would re-subscribe the
  // auth listener on each render.
  const chatRef = useRef(chat);
  useEffect(() => {
    chatRef.current = chat;
  });

  // One-time init guard as a ref, so initLiveChat stays referentially stable.
  const initializingRef = useRef(false);

  const initLiveChat = useCallback(async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    setIsInitializing(true);
    try {
      const sid = await getOrCreateChatSession();
      setSessionId(sid);

      const existingMessages = await loadChatMessages(sid);
      if (existingMessages && existingMessages.length > 0) {
        const uiMessages = existingMessages.map((msg: any) => ({
          id: msg.id,
          role: msg.role as "user" | "assistant" | "system",
          parts: msg.parts?.length > 0
            ? msg.parts
            : [{ type: "text" as const, text: msg.content || "" }],
        }));
        chatRef.current.setMessages(uiMessages);
      }

      setMode("live");
      posthog.capture("chat_initialized", {
        page,
        has_history: existingMessages && existingMessages.length > 0,
        ...carrierProps,
      });
    } catch (err) {
      console.error("Error creating chat session:", err);
    } finally {
      initializingRef.current = false;
      setIsInitializing(false);
    }
    // carrierProps is derived from carrierId/carrierName; page is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierId, carrierName, page]);

  // Check auth on mount. Use getSession() (local storage read) rather than
  // getUser() (network + supabase-js navigator.locks): getUser can hang
  // indefinitely on a stuck token-refresh lock held by another tab, which froze
  // the chat skeleton forever when a second tab was opened. This is only a
  // UI-init decision — the server still calls getUser() for the real gate. The
  // 3s time-box stays as a belt-and-suspenders fallback.
  useEffect(() => {
    let settled = false;
    const markChecked = () => {
      if (settled) return;
      settled = true;
      setAuthChecked(true);
    };
    const timer = setTimeout(markChecked, 3000);
    browserClient.auth.getSession().then(({ data: { session } }) => {
      setInitialUser(session?.user ? { id: session.user.id } : null);
    }).catch(() => {
      // Stale token or storage error — continue as unauthenticated
    }).finally(() => {
      clearTimeout(timer);
      markChecked();
    });
  }, []);

  // Decide how to start the conversation, once auth (and — for anon visitors —
  // the flag) has resolved. Runs at most once via initStartedRef.
  useEffect(() => {
    if (!authChecked || initStartedRef.current) return;

    if (initialUser) {
      initStartedRef.current = true;
      // Kicks off async live-chat init on mount for a signed-in visitor; the
      // synchronous setIsInitializing(true) inside is the intended loading flag.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      initLiveChat();
      return;
    }

    // Anonymous visitor: DEFER. Do not sign in anonymously or create a session
    // on mount — that minted a throwaway anon auth user + an empty chat_session
    // (and a Slack new_chat_session ping) for every passive page view. Anon
    // sign-in + session creation now happen lazily in handleInteraction on the
    // first real message. Stay static: free-anon shows the input; legacy
    // (flag-off) shows the first-message auth gate. Leave initStartedRef unset
    // so the lazy path runs.
  }, [authChecked, initialUser, freeAnonChat, initLiveChat]);

  // Settle the flag once it resolves, or after a short grace so a legacy
  // (flag-off, no PostHog) visitor still falls through to the static UI.
  useEffect(() => {
    if (freeAnonChat !== undefined) return;
    const t = setTimeout(() => setGraceElapsed(true), 1200);
    return () => clearTimeout(t);
  }, [freeAnonChat]);

  // Listen for auth state changes (auth-gate → live, and sign-out reset).
  useEffect(() => {
    const { data: { subscription } } = browserClient.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && mode === "auth-gate") {
        initLiveChat();
      }
      // Just verified (anon → real) after tapping a gated contact CTA — re-request
      // the contact so the tool reveals it as a verified user. (#486)
      if (event === "SIGNED_IN" && session?.user && !session.user.is_anonymous) {
        // Carry the carrier tag so the model reliably calls getCarrierContactTool
        // for the exact carrier (the [carrier:UUID] trigger the alternatives flow
        // already uses) instead of parroting the verify line as plain text.
        const revealCarrierId = consumePendingContactReveal();
        if (revealCarrierId) {
          chatRef.current.sendMessage({ text: `[carrier:${revealCarrierId}] Ya verifiqué mi número, pásame el contacto directo` });
        }
      }
      if (event === "SIGNED_OUT") {
        setMode("static");
        setSessionId(null);
        chatRef.current.setMessages([]);
      }
    });
    return () => subscription.unsubscribe();
  }, [mode, initLiveChat]);

  // Auto-scroll on new messages — scoped to the messages container, not the page.
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [chat.messages]);

  // Refocus textarea when streaming finishes. `preventScroll` so refocusing a
  // sticky/below-the-fold panel never scrolls the whole page (#496 — the Enter
  // "jump"): the browser's default focus-into-view is what moved the page.
  useEffect(() => {
    if (chat.status === "ready") {
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [chat.status]);

  // Send pending message after transitioning to live.
  useEffect(() => {
    if (mode === "live" && pendingText && sessionId && !isInitializing) {
      chatRef.current.sendMessage({ text: pendingText });
      // Clear the one-shot send queue after dispatching it; sending is the
      // effect's purpose, so this state write is intentional.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingText(null);
    }
  }, [mode, pendingText, sessionId, isInitializing]);

  const handleInteraction = (text: string) => {
    if (mode === "live") {
      // Per-session cap (#486): stop before spending an LLM call; server enforces too.
      const userMsgCount = chat.messages.filter((m) => m.role === "user").length;
      const limitsDisabled = process.env.NEXT_PUBLIC_CHAT_LIMITS_DISABLED === "1";
      if (!limitsDisabled && userMsgCount >= MAX_USER_MESSAGES) {
        setLimitReached("session");
        setInputValue("");
        return;
      }
      chat.sendMessage({ text });
      setInputValue("");
      return;
    }

    if (freeAnonChat) {
      // Free anon chat: no gate. Queue the message; the pending-text effect sends
      // it once the live session is ready. `pendingText` set here (synchronously)
      // is what drives the immediate loading state (isSending) — no dead air while
      // the anon session initializes.
      setPendingText(text);
      setInputValue("");
      if (!initStartedRef.current && !isInitializing) {
        initStartedRef.current = true;
        // Time-box the session read so a contended navigator.locks auth lock
        // (held by another same-origin tab mid token-refresh) can't hang the
        // send. On timeout OR an existing session, go live and let the server
        // (cookie auth, no lock) resolve the user; only a confirmed no-session
        // mints a new anon identity.
        (async () => {
          const TIMEOUT = Symbol("timeout");
          const res: unknown = await Promise.race([
            browserClient.auth.getSession().then(({ data }) => data.session),
            new Promise((r) => setTimeout(() => r(TIMEOUT), 2500)),
          ]);
          if (res !== TIMEOUT && !res) {
            const { error } = await browserClient.auth.signInAnonymously();
            if (error) {
              console.error("Anonymous sign-in failed:", error);
              initStartedRef.current = false;
              return;
            }
          }
          initLiveChat();
        })();
      }
      return;
    }

    // Legacy: not authenticated — gate + open auth modal.
    posthog.capture("auth_gate_shown", { page, trigger: text, ...carrierProps });
    setPendingText(text);
    setMode("auth-gate");
    openAuthDialog("signup", undefined, "chat");
  };

  const handleChipClick = (chipText: string) => {
    const isFollowup = mode === "live" && chat.messages.length > 0;
    posthog.capture("chip_clicked", {
      label: chipText,
      source: isFollowup ? "followup" : "landing",
      page,
      ...carrierProps,
    });
    handleInteraction(chipText);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    handleInteraction(inputValue.trim());
    setInputValue("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleAuthClick = () => {
    openAuthDialog("signup", undefined, "chat");
  };

  // Once there are messages, transition from landing to full chat.
  const hasConversation = mode === "live" && chat.messages.length > 0;

  // Skeleton while checking auth AND while a going-live visitor initializes, so we
  // don't flash the static landing before the chat becomes live. Anon visitors
  // (either flag state) land on the static UI, so only a signed-in visitor (eager
  // live init) or an unsettled flag keeps the skeleton up.
  const goingLive = initialUser !== null || !flagSettled;
  const showSkeleton = !authChecked || (mode !== "live" && goingLive);

  // Loading-state fix (Phase 5): a queued message (pendingText, set synchronously
  // on click) OR an in-flight request means "sending" — drives the pending bubble,
  // typing dots, and input lock immediately, before the async anon-init resolves.
  const isBusy = chat.status === "streaming" || chat.status === "submitted";
  const isSending = pendingText !== null || isBusy;
  // Show the queued user's bubble until it lands in the live message list.
  const showPendingBubble = pendingText !== null && mode !== "live";

  return {
    // state
    mode,
    sessionId,
    inputValue,
    setInputValue,
    limitReached,
    pendingText,
    // chat
    messages: chat.messages,
    status: chat.status,
    sendMessage: (text: string) => chat.sendMessage({ text }),
    // derived
    isInitializing,
    isBusy,
    isSending,
    showPendingBubble,
    hasConversation,
    showSkeleton,
    freeAnonChat,
    // handlers
    handleChipClick,
    handleSubmit,
    handleAuthClick,
    // refs
    textareaRef,
    messagesContainerRef,
  };
}
